import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Tune as TuneIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  distributionService,
  type DistributionPoliciesSnapshot,
  type SampleSizeCostBracketRow,
  type SampleSizePolicyRow,
  type StoreLeadTimeRow,
  type StoreMinStockPolicyRow,
} from "../services/distributionService";
import { usePageTitle } from "../context/PageTitleContext";
import {
  type PolicyDef,
  POLICIES,
  GROUP_LABELS,
} from "./configuracion-distribucion/policies.data";
import { PolicyRow } from "./configuracion-distribucion/PolicyRow";

/**
 * Página admin para configurar los 21 parámetros §7 lluvia + matrices de
 * stock mínimo, tamaños de muestra, brackets de costo y lead times por tienda.
 * Persiste vía PUT /api/config/distribution-policies.
 *
 * Diseñada para que Luis configure una sola vez los parámetros (P14 alcance
 * completo). No reduce alcance — cada parámetro de la lluvia tiene un campo.
 */


export function ConfiguracionDistribucionPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const queryClient = useQueryClient();

  useEffect(() => {
    setTitle("Configuración — Distribución");
    return () => setTitle("");
  }, [setTitle]);

  const {
    data: snapshot,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["distribution-policies"],
    queryFn: () => distributionService.getDistributionPolicies(),
  });

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const initialValues = useMemo(() => {
    const out: Record<string, string> = {};
    if (snapshot?.global) {
      for (const k of Object.keys(snapshot.global)) {
        out[k] = String(snapshot.global[k] ?? "");
      }
    }
    return out;
  }, [snapshot?.global]);

  useEffect(() => {
    setDrafts(initialValues);
  }, [initialValues]);

  const setMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      distributionService.setDistributionPolicy(key, value),
    onSuccess: (_, vars) => {
      setSnackbar({
        open: true,
        message: `'${vars.key}' guardado.`,
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["distribution-policies"] });
    },
    onError: (err: unknown, vars) => {
      const detail =
        err instanceof Error ? err.message : "Error al guardar.";
      setSnackbar({
        open: true,
        message: `'${vars.key}': ${detail}`,
        severity: "error",
      });
    },
  });

  const saveOne = (def: PolicyDef): void => {
    const raw = drafts[def.key] ?? "";
    let value: unknown = raw;
    if (def.type === "number" || def.type === "int") {
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        setSnackbar({
          open: true,
          message: `'${def.key}': valor no numérico.`,
          severity: "error",
        });
        return;
      }
      value = num;
    } else if (def.type === "string_array") {
      // B4.4: central_warehouses se edita como CSV; el backend espera un array.
      const arr = raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (arr.length === 0) {
        setSnackbar({
          open: true,
          message: `'${def.key}': al menos un valor requerido.`,
          severity: "error",
        });
        return;
      }
      value = arr;
    }
    setMutation.mutate({ key: def.key, value });
  };

  const resetOne = (key: string): void => {
    setDrafts((prev) => ({ ...prev, [key]: initialValues[key] ?? "" }));
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !snapshot) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              onClick={() => refetch()}
              startIcon={<RefreshIcon />}
            >
              Reintentar
            </Button>
          }
        >
          No se pudieron cargar las políticas de distribución.
        </Alert>
      </Box>
    );
  }

  const groupedPolicies = POLICIES.reduce<Record<string, PolicyDef[]>>(
    (acc, def) => {
      if (!acc[def.group]) acc[def.group] = [];
      acc[def.group]!.push(def);
      return acc;
    },
    {},
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <TuneIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Configuración — Distribución
          </Typography>
        </Stack>

        <Alert severity="info" variant="outlined">
          Estos parámetros afectan el cálculo de compras, ROP, muestras,
          denegaciones y criticidad. Cambian el comportamiento del módulo
          completo. Los cambios persisten en <code>distribution_policies</code>{" "}
          y se invalidan en cache automáticamente.
        </Alert>

        {Object.entries(GROUP_LABELS).map(([group, label]) => {
          const policies = groupedPolicies[group as PolicyDef["group"]] ?? [];
          if (policies.length === 0) return null;
          return (
            <Paper key={group} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                {label}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {policies.map((def) => (
                  <PolicyRow
                    key={def.key}
                    def={def}
                    value={drafts[def.key] ?? ""}
                    initial={initialValues[def.key] ?? ""}
                    onChange={(v) =>
                      setDrafts((prev) => ({ ...prev, [def.key]: v }))
                    }
                    onSave={() => saveOne(def)}
                    onReset={() => resetOne(def.key)}
                    saving={setMutation.isPending}
                  />
                ))}
              </Stack>
            </Paper>
          );
        })}

        <MatricesPanel snapshot={snapshot} />
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


function MatricesPanel({
  snapshot,
}: {
  snapshot: DistributionPoliciesSnapshot;
}): React.ReactElement {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        Matrices y catálogos (lectura)
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
        Estas tablas se editan vía migraciones / seeds. Esta vista es
        para verificación.
      </Typography>

      <Stack spacing={3} sx={{ mt: 1 }}>
        <MatrixSection
          title="Stock mínimo (ABC × tamaño)"
          rows={snapshot.min_stock_matrix}
          columns={[
            { key: "abc", label: "ABC" },
            { key: "tamano", label: "Tamaño" },
            { key: "min_qty", label: "Min qty" },
          ]}
          rowKey={(row: StoreMinStockPolicyRow) => `${row.abc}|${row.tamano}`}
        />

        <MatrixSection
          title="Tamaño de muestra (ABC × tamaño)"
          rows={snapshot.sample_size_matrix}
          columns={[
            { key: "abc", label: "ABC" },
            { key: "tamano", label: "Tamaño" },
            { key: "base_qty", label: "Base qty" },
          ]}
          rowKey={(row: SampleSizePolicyRow) => `${row.abc}|${row.tamano}`}
        />

        <MatrixSection
          title="Brackets de costo"
          rows={snapshot.cost_brackets}
          columns={[
            { key: "name", label: "Nombre" },
            { key: "min_cost", label: "Min costo" },
            { key: "max_cost", label: "Max costo" },
            { key: "factor", label: "Factor" },
            { key: "floor_qty", label: "Floor" },
            { key: "use_inverse_formula", label: "Inversa" },
            { key: "constant_k", label: "K" },
          ]}
          rowKey={(row: SampleSizeCostBracketRow) => `${row.id}`}
        />

        <MatrixSection
          title="Lead times por tienda (días)"
          rows={snapshot.lead_times}
          columns={[
            { key: "mxp_code", label: "Tienda" },
            { key: "days", label: "Días" },
          ]}
          rowKey={(row: StoreLeadTimeRow) => row.mxp_code}
        />
      </Stack>
    </Paper>
  );
}

interface MatrixSectionProps<T> {
  title: string;
  rows: T[];
  columns: Array<{ key: keyof T & string; label: string }>;
  rowKey: (row: T) => string;
}

function MatrixSection<T>({
  title,
  rows,
  columns,
  rowKey,
}: MatrixSectionProps<T>): React.ReactElement {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={500} gutterBottom>
        {title}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key}>{c.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="caption" color="text.secondary">
                    Sin datos. Verifica seeds.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={rowKey(row)}>
                {columns.map((c) => {
                  const cell = (row as Record<string, unknown>)[c.key];
                  return (
                    <TableCell key={c.key}>
                      {cell === null || cell === undefined ? "" : String(cell)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
